import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Play, CheckCircle2, ArrowRight, ShieldCheck } from 'lucide-react';
import { SITE } from '../mock';

// VSL video — Wistia embed ("VSL FINAL RE")
const VIDEO_EMBED_URL = 'https://fast.wistia.net/embed/iframe/7mh4powapo';

// Razorpay payment link — opens Razorpay checkout in a new tab
// Razorpay dashboard is configured to redirect to /thankyou after successful payment
const PAYMENT_LINK = 'https://rzp.io/rzp/MK9dwMg';

export default function TrainingPage() {
  return (
    <div className="min-h-screen bg-navy-950 text-white">
      {/* Ambient glow */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[1100px] h-[700px] rounded-full bg-amber-300/[0.06] blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-amber-500/[0.04] blur-3xl" />
      </div>

      {/* Back link */}
      <div className="relative max-w-5xl mx-auto px-6 pt-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-amber-300 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to home
        </Link>
      </div>

      <main className="relative max-w-5xl mx-auto px-6 pt-10 pb-24">
        {/* Brand monogram */}
        <div className="flex flex-col items-center text-center">
          <div className="relative">
            <div className="absolute -inset-6 bg-amber-300/10 blur-2xl rounded-full" />
            <div className="relative w-20 h-20 rounded-2xl border border-amber-300/30 bg-gradient-to-b from-navy-900 to-navy-950 flex items-center justify-center">
              <span className="font-serif text-3xl bg-gradient-to-b from-amber-100 to-amber-400 bg-clip-text text-transparent">
                NT
              </span>
            </div>
          </div>
          <p className="mt-4 text-[11px] tracking-[0.28em] uppercase bg-gradient-to-b from-amber-100 to-amber-400 bg-clip-text text-transparent font-semibold">
            Nishit Thakkar &middot; Manifestation Mastermind
          </p>
        </div>

        {/* Headline */}
        <h1 className="mt-10 font-serif text-center text-white text-[34px] md:text-[54px] leading-[1.05] tracking-tight">
          <span className="block bg-gradient-to-b from-white to-neutral-400 bg-clip-text text-transparent">
            We Hand-Hold Ambitious Souls
          </span>
          <span className="block bg-gradient-to-b from-white to-neutral-400 bg-clip-text text-transparent">
            To Command Wealth With{' '}
            <span className="inline-block bg-gradient-to-b from-amber-100 to-amber-400 bg-clip-text text-transparent italic">
              Proven Systems.
            </span>
          </span>
        </h1>

        {/* USP badge */}
        <p className="mt-6 text-center font-serif text-[20px] md:text-[26px] bg-gradient-to-b from-amber-100 to-amber-400 bg-clip-text text-transparent">
          Manifestation Meets Business Implementation&trade;
        </p>

        {/* Video / VSL section */}
        <div className="mt-12 mx-auto max-w-4xl">
          <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-navy-900 shadow-[0_30px_80px_-20px_rgba(251,191,36,0.18)]">
            <div className="aspect-video w-full bg-gradient-to-br from-navy-900 via-navy-950 to-navy-900 relative">
              {VIDEO_EMBED_URL ? (
                <iframe
                  src={VIDEO_EMBED_URL}
                  title="Manifestation Mastermind VSL"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              ) : (
                <VideoPlaceholder />
              )}
            </div>
          </div>
          <p className="mt-3 text-center text-[12px] tracking-[0.22em] uppercase text-neutral-500">
            Watch the full training before booking your call
          </p>

          {/* Book Now — immediately below the VSL */}
          <div className="mt-7 flex flex-col items-center">
            <a
              href={PAYMENT_LINK}
              rel="noreferrer"
              className="group inline-flex items-center gap-2 rounded-full px-10 py-4 text-[16px] font-semibold text-neutral-900 bg-gradient-to-b from-amber-200 to-amber-400 shadow-[0_12px_40px_-8px_rgba(251,191,36,0.6)] hover:-translate-y-[2px] hover:shadow-[0_18px_50px_-8px_rgba(251,191,36,0.8)] transition-all"
            >
              BOOK NOW
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <p className="mt-3 text-[11px] tracking-[0.2em] uppercase text-amber-300/70">
              Secure checkout via Razorpay · Limited slots per cohort
            </p>
          </div>
        </div>

        {/* How to get started */}
        <section className="mt-20">
          <h2 className="font-serif text-white text-[32px] md:text-[44px] tracking-tight">
            How to get started
          </h2>
          <ul className="mt-8 space-y-5">
            {STEPS.map((s, i) => (
              <li key={i} className="flex items-start gap-4">
                <span className="shrink-0 mt-1 w-6 h-6 rounded-full bg-amber-300/15 border border-amber-300/30 flex items-center justify-center text-amber-300">
                  <CheckCircle2 size={14} strokeWidth={2} />
                </span>
                <p className={`text-[15px] md:text-[17px] leading-relaxed ${s.muted ? 'text-neutral-400' : 'text-white'}`}>
                  {s.text}
                </p>
              </li>
            ))}
          </ul>
        </section>

        {/* Single prominent Payment CTA → Razorpay */}
        <section className="mt-16">
          <a
            href={PAYMENT_LINK}
            rel="noreferrer"
            className="group relative block overflow-hidden rounded-3xl border border-amber-300/40 bg-gradient-to-br from-amber-300/15 via-amber-400/8 to-transparent p-8 md:p-12 transition-all hover:-translate-y-[2px] hover:border-amber-300/70"
          >
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-amber-300/20 blur-3xl group-hover:bg-amber-300/30 transition-colors" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-amber-500/10 blur-3xl" />

            <div className="relative flex flex-col md:flex-row md:items-center gap-8 md:gap-10">
              <div className="flex-1">
                <p className="text-[11px] tracking-[0.25em] uppercase text-amber-300">
                  Ready to begin?
                </p>
                <h3 className="mt-3 font-serif text-white text-[30px] md:text-[44px] leading-[1.1] tracking-tight">
                  Book Your Call &amp; <span className="italic text-amber-200">Reserve Your Slot</span>
                </h3>
                <p className="mt-4 text-neutral-300 text-[14px] md:text-[16px] leading-relaxed max-w-xl">
                  Complete your registration to lock in your 1-to-1 consulting slot. Limited
                  applications accepted per cohort.
                </p>

                <div className="mt-7 inline-flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-[15px] font-semibold text-navy-900 bg-gradient-to-b from-amber-200 to-amber-400 shadow-[0_10px_36px_-6px_rgba(251,191,36,0.55)] group-hover:shadow-[0_16px_44px_-6px_rgba(251,191,36,0.75)] transition-all">
                    Book A Call <ArrowRight size={16} />
                  </span>
                  <span className="inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-amber-300/90">
                    <ShieldCheck size={14} /> Secure Razorpay Checkout
                  </span>
                </div>
              </div>

              <div className="md:w-48 shrink-0 flex md:flex-col items-center md:items-end gap-3 md:text-right">
                <div className="font-serif text-white text-[40px] md:text-[52px] leading-none">
                  ₹
                </div>
                <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-400">
                  Pay via UPI / Card / Net&nbsp;Banking
                </p>
              </div>
            </div>
          </a>
        </section>

        {/* Contact */}
        <p className="mt-14 text-center text-neutral-400 text-sm">
          Any questions?{' '}
          <a className="text-amber-300 hover:underline underline-offset-4" href="mailto:info@nishitthakkar.in">
            info@nishitthakkar.in
          </a>
        </p>

        {/* Footer */}
        <footer className="mt-16 border-t border-white/5 pt-6 text-center">
          <div className="flex items-center justify-center gap-5 text-[12px] text-neutral-500">
            <span>Privacy Policy</span>
            <span className="text-neutral-700">|</span>
            <span>Terms &amp; Conditions</span>
          </div>
          <p className="mt-4 max-w-2xl mx-auto text-[11px] text-neutral-600 leading-relaxed">
            DISCLAIMER: NishitThakkar.in does not guarantee any specific results or outcomes. Any
            client examples shown are for demonstration / educational purposes only and must not be
            interpreted as promised or expected outcomes. Your results depend on your effort,
            mindset work, and consistent implementation.
          </p>
          <p className="mt-4 text-[12px] text-neutral-500">
            &copy; {new Date().getFullYear()} {SITE.brandTop} {SITE.brandBottom}. All Rights Reserved.
          </p>
        </footer>
      </main>
    </div>
  );
}

const STEPS = [
  { text: 'Watch the full training video above — carefully and without distraction.' },
  { text: 'Click "Book A Call" below and complete your secure Razorpay payment.' },
  { text: "Right after payment you'll be taken to the application form to share your story." },
  { text: "We'll review your application and reach out within 48 hours if you're a good fit.", muted: true },
  { text: "If selected and you miss your scheduled slot, you'll lose your priority placement.", muted: true },
  { text: 'Only a limited number of clients are accepted per cohort to ensure deep 1-to-1 focus.', muted: true },
  { text: 'The next cohort opens soon — exact date will be shared on the call.', muted: true },
];

function VideoPlaceholder() {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(251,191,36,0.08),transparent_60%)]" />
      <button
        type="button"
        aria-label="Play training video (coming soon)"
        className="group relative w-24 h-24 md:w-28 md:h-28 rounded-2xl bg-gradient-to-b from-amber-200 to-amber-400 flex items-center justify-center shadow-[0_20px_50px_-10px_rgba(251,191,36,0.6)] hover:-translate-y-[2px] transition-all"
      >
        <Play size={36} className="text-navy-900 translate-x-[2px]" strokeWidth={2.2} fill="currentColor" />
      </button>
      <p className="relative mt-6 font-serif text-white text-2xl md:text-3xl">
        Training Video <span className="italic text-amber-200">Coming Soon</span>
      </p>
      <p className="relative mt-2 text-neutral-400 text-sm max-w-md">
        Nishit's full VSL will be embedded here once recorded. Bookmark this page and check back
        shortly.
      </p>
    </div>
  );
}
